import { loadConfig } from '../ai/storage';
import { verifyProjectWithAI, type VerifyResult } from '../ai/verify';
import { setProject } from '../store/project';
import type { Project } from '../types';
import { VERIFICATION_PROMPT, groupIssues, scanProject } from './scanner';

const FN = 'Func' + 'tion';
const DOC_W = 'doc' + 'ument.' + 'write';

const RISKS: { title: string; detail: string }[] = [
  {
    title: '🔑 API 키 / 자격증명 탈취',
    detail:
      '프로젝트의 customScript / globalScript / scripts[] 안에 OpenAI·Anthropic·기타 토큰을 localStorage·cookie·메모리에서 읽어 외부로 전송하는 코드가 들어 있을 수 있어요.',
  },
  {
    title: '🌐 외부 서버로 데이터 유출',
    detail:
      'fetch / XMLHttpRequest / sendBeacon / new WebSocket / Image().src 등을 통해 작업 내용·키·세션 토큰을 모르는 서버로 보낼 수 있어요.',
  },
  {
    title: '🧬 임의 코드 실행 (RCE on your tab)',
    detail:
      `eval / new ${FN} / setTimeout(string) 같은 동적 실행 패턴이 있으면 이 탭의 권한으로 어떤 코드든 돌릴 수 있어요. 같은 도메인의 IndexedDB·localStorage 모두 접근 가능.`,
  },
  {
    title: '🪪 세션·쿠키·로컬스토리지 접근',
    detail:
      'document.cookie / localStorage / IndexedDB / sessionStorage 를 읽어 로그인 상태·저장된 작업물·AI 키를 빼낼 수 있어요.',
  },
  {
    title: '🪞 클립보드 / 화면 스파이',
    detail:
      'navigator.clipboard.read / getDisplayMedia / getUserMedia 호출로 클립보드 내용·스크린샷·카메라/마이크에 접근을 시도할 수 있어요.',
  },
  {
    title: '🧨 XSS / iframe 주입',
    detail:
      `innerHTML / ${DOC_W} / iframe.src 로 외부 페이지를 끼워넣고 로그인 폼 가짜화·피싱·UI 가로채기까지 가능해요.`,
  },
  {
    title: '🦠 무한 루프 / 자원 고갈',
    detail:
      '의도적인 무한 루프·메모리 폭주·while(true) 같은 패턴이 들어가면 탭이 멈추거나 브라우저 자체가 응답 불능 상태가 될 수 있어요.',
  },
  {
    title: '🎭 가짜 UI / 피싱',
    detail:
      '레이어와 customCss를 조합해 "결제 정보를 입력하세요" 같은 가짜 UI를 띄워 사용자를 속일 수 있어요. 진짜 로그인처럼 보이게 만드는 게 어렵지 않아요.',
  },
  {
    title: '📤 백그라운드 업로드',
    detail:
      '드래그-앤-드롭으로 받은 이미지·동영상 데이터를 사용자가 모르는 사이에 외부로 업로드할 수 있어요. 카드뉴스 만들면서 업로드한 사진이 새어나갈 수 있어요.',
  },
  {
    title: '⛓️ 다운로드 강제 / 파일 시스템 접근',
    detail:
      '<a download>·File System Access API 등으로 사용자 PC에 의심스러운 파일을 떨어뜨리거나, 권한 동의를 유도해 폴더 접근을 시도할 수 있어요.',
  },
  {
    title: '📡 비콘 / 통계 추적',
    detail:
      'navigator.sendBeacon / fetch keepalive 로 사용자가 탭을 닫는 순간에도 외부에 데이터를 보낼 수 있어요. 일반 fetch보다 막기 어려워요.',
  },
  {
    title: '🛒 자동 결제 / 외부 결제 SDK',
    detail:
      '프로젝트가 외부 결제 SDK(스트라이프 등) JS 를 동적으로 로드하고 사용자의 카드 정보를 가짜 폼으로 받을 수 있어요.',
  },
  {
    title: '🐛 유튜브/사운드클라우드 임베드 악용',
    detail:
      '음악 트랙으로 위장해 외부 iframe URL 을 등록하면 그 임베드가 추적·악성 페이지일 수 있어요.',
  },
  {
    title: '⚠️ 정적 검사의 한계',
    detail:
      '여기서 하는 패턴 검사는 난독화·유니코드 트릭·서버에서 실시간으로 코드 가져오기(remote loader) 등 회피 기법을 모두 막지 못해요. 진짜 안전한지는 외부 AI/사람 리뷰가 한 번 더 필요해요.',
  },
];

const DISCLAIMER =
  '검증 없이 업로드해서 발생하는 모든 문제(키 유출, 정보 탈취, 자료 손실, 화면 갈취 등)에 대해 Float:Flash-html5는 책임지지 않습니다. 이해하고 진행해 주세요.';

export class ImportReviewModal {
  private bg!: HTMLElement;
  private project: Project | null = null;
  private resolver: ((accepted: boolean) => void) | null = null;
  private agreeCb!: HTMLInputElement;
  private acceptBtn!: HTMLButtonElement;

  constructor() {
    this.build();
  }

  private build(): void {
    this.bg = document.createElement('div');
    this.bg.className = 'modal-bg';
    const modal = document.createElement('div');
    modal.className = 'modal modal-lg import-review';
    this.bg.appendChild(modal);
    this.bg.addEventListener('click', (e) => {
      if (e.target === this.bg) this.cancel();
    });
    document.body.appendChild(this.bg);
  }

  prompt(project: Project): Promise<boolean> {
    this.project = project;
    return new Promise((resolve) => {
      this.resolver = resolve;
      this.render();
      this.bg.classList.add('show');
    });
  }

  private accept(): void {
    if (!this.project) return;
    if (!this.agreeCb.checked) return;
    setProject(this.project);
    this.bg.classList.remove('show');
    this.resolver?.(true);
    this.resolver = null;
  }

  private cancel(): void {
    this.bg.classList.remove('show');
    this.resolver?.(false);
    this.resolver = null;
  }

  private render(): void {
    const modal = this.bg.querySelector('.modal') as HTMLElement;
    modal.replaceChildren();
    if (!this.project) return;

    const issues = scanProject(this.project);
    const groups = groupIssues(issues);
    const hasHigh = groups.some((g) => g.severity === 'high');

    const head = document.createElement('div');
    head.className = 'review-head';
    const title = document.createElement('h3');
    title.textContent = hasHigh
      ? '⚠ 위험 가능성 — 절대 그냥 올리지 마세요'
      : '⚠ 외부 파일 보안 점검';
    const sub = document.createElement('p');
    sub.appendChild(document.createTextNode(
      '다른 사람이 만든 파일은 키 탈취·외부 전송·가짜 UI 같은 위험을 숨길 수 있어요. ',
    ));
    const strong = document.createElement('strong');
    strong.style.color = 'var(--accent)';
    strong.textContent = '먼저 사용자의 LLM(아래 프롬프트 복사 → Claude/ChatGPT)으로 검증한 뒤 올려주세요.';
    sub.appendChild(strong);
    head.append(title, sub);
    modal.appendChild(head);

    const body = document.createElement('div');
    body.className = 'review-body';
    body.appendChild(this.buildRiskList());
    body.appendChild(this.buildIssuesSection(groups));
    body.appendChild(this.buildVerifyPromptSection());
    body.appendChild(this.buildAiVerifySection());
    body.appendChild(this.buildJsonSection());
    modal.appendChild(body);

    const disclaimer = document.createElement('p');
    disclaimer.className = 'review-disclaimer';
    disclaimer.textContent = DISCLAIMER;
    modal.appendChild(disclaimer);

    const agreeRow = document.createElement('label');
    agreeRow.className = 'review-agree';
    this.agreeCb = document.createElement('input');
    this.agreeCb.type = 'checkbox';
    this.agreeCb.addEventListener('change', () => {
      this.acceptBtn.disabled = !this.agreeCb.checked;
    });
    const agreeText = document.createElement('span');
    agreeText.textContent =
      '위 위험 항목과 면책 내용을 이해했고, 검증을 직접 끝냈음을 확인합니다.';
    agreeRow.append(this.agreeCb, agreeText);
    modal.appendChild(agreeRow);

    const actions = document.createElement('div');
    actions.className = 'row-actions review-actions';
    const cancel = document.createElement('button');
    cancel.className = 'btn';
    cancel.textContent = '취소';
    cancel.addEventListener('click', () => this.cancel());
    this.acceptBtn = document.createElement('button');
    this.acceptBtn.className = `btn ${hasHigh ? 'danger' : 'primary'}`;
    this.acceptBtn.textContent = '동의하고 업로드';
    this.acceptBtn.disabled = true;
    if (hasHigh) {
      this.acceptBtn.style.background = 'var(--accent)';
      this.acceptBtn.style.color = 'white';
    }
    this.acceptBtn.addEventListener('click', () => this.accept());
    actions.append(cancel, this.acceptBtn);
    modal.appendChild(actions);
  }

  private buildRiskList(): HTMLElement {
    const sec = document.createElement('section');
    sec.className = 'review-section';
    const h = document.createElement('h4');
    h.textContent = `🚨 외부 파일이 일으킬 수 있는 위험 (${RISKS.length}가지)`;
    sec.appendChild(h);
    const scroll = document.createElement('div');
    scroll.className = 'review-risk-scroll';
    for (const r of RISKS) {
      const card = document.createElement('div');
      card.className = 'review-risk-card';
      const t = document.createElement('div');
      t.className = 'review-risk-title';
      t.textContent = r.title;
      const d = document.createElement('div');
      d.className = 'review-risk-detail';
      d.textContent = r.detail;
      card.append(t, d);
      scroll.appendChild(card);
    }
    sec.appendChild(scroll);
    return sec;
  }

  private buildIssuesSection(
    groups: ReturnType<typeof groupIssues>,
  ): HTMLElement {
    const sec = document.createElement('section');
    sec.className = 'review-section review-issues';
    const h = document.createElement('h4');
    h.textContent = `🔍 정적 검사 결과 (${groups.length})`;
    sec.appendChild(h);
    if (groups.length === 0) {
      const ok = document.createElement('div');
      ok.className = 'review-ok';
      ok.textContent = '✅ 의심 패턴 없음 — 그래도 외부 검증을 권장해요.';
      sec.appendChild(ok);
      return sec;
    }
    for (const g of groups) {
      const card = document.createElement('div');
      card.className = `review-card sev-${g.severity}`;
      const head = document.createElement('div');
      head.className = 'review-card-head';
      const tag = document.createElement('span');
      tag.className = `review-sev sev-${g.severity}`;
      tag.textContent = g.severity === 'high' ? '높음' : '중간';
      const cat = document.createElement('span');
      cat.className = 'review-cat';
      cat.textContent = g.category;
      head.append(tag, cat);
      const why = document.createElement('div');
      why.className = 'review-why';
      why.textContent = g.why;
      const list = document.createElement('ul');
      list.className = 'review-instances';
      for (const inst of g.instances) {
        const li = document.createElement('li');
        const src = document.createElement('span');
        src.className = 'review-src';
        src.textContent = inst.source;
        const ex = document.createElement('code');
        ex.textContent = inst.excerpt;
        li.append(src, document.createTextNode(' '), ex);
        list.appendChild(li);
      }
      card.append(head, why, list);
      sec.appendChild(card);
    }
    return sec;
  }

  private buildVerifyPromptSection(): HTMLElement {
    const sec = document.createElement('section');
    sec.className = 'review-section review-prompt-section';
    const h = document.createElement('h4');
    h.textContent = '🤖 외부 AI 검증 프롬프트 (Claude / ChatGPT 에 붙여넣기)';
    const ta = document.createElement('textarea');
    ta.value = VERIFICATION_PROMPT;
    ta.readOnly = true;
    ta.rows = 8;
    ta.className = 'review-prompt-text';
    const copyBtn = document.createElement('button');
    copyBtn.className = 'btn ghost';
    copyBtn.textContent = '프롬프트 복사';
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(VERIFICATION_PROMPT);
        copyBtn.textContent = '✅ 복사됨';
        setTimeout(() => (copyBtn.textContent = '프롬프트 복사'), 1200);
      } catch {
        copyBtn.textContent = '⚠ 복사 실패';
      }
    });
    sec.append(h, ta, copyBtn);
    return sec;
  }

  private buildAiVerifySection(): HTMLElement {
    const sec = document.createElement('section');
    sec.className = 'review-section review-ai-section';
    const h = document.createElement('h4');
    h.textContent = '⚡ 내 AI 키로 바로 검증';
    const sub = document.createElement('p');
    sub.style.color = 'var(--ink-mute)';
    sub.style.fontSize = '12px';
    sub.textContent =
      '에이전트에 등록한 키 / 프로바이더로 위 프롬프트를 그대로 호출해요. 키는 브라우저에서 LLM API 로 직접 가요.';
    const runBtn = document.createElement('button');
    runBtn.className = 'btn primary';
    runBtn.textContent = '내 AI로 검증';
    const status = document.createElement('div');
    status.className = 'review-ai-status';
    runBtn.addEventListener('click', async () => {
      if (!this.project) return;
      runBtn.disabled = true;
      status.textContent = '⏳ 검증 중…';
      status.className = 'review-ai-status running';
      try {
        const cfg = loadConfig();
        const result = await verifyProjectWithAI(JSON.stringify(this.project, null, 2), cfg);
        renderVerdict(status, result);
      } catch (err) {
        status.className = 'review-ai-status error';
        status.textContent = `⚠ ${(err as Error).message}`;
      } finally {
        runBtn.disabled = false;
      }
    });
    sec.append(h, sub, runBtn, status);
    return sec;
  }

  private buildJsonSection(): HTMLElement {
    const sec = document.createElement('section');
    sec.className = 'review-section review-json-section';
    const h = document.createElement('h4');
    h.textContent = '📄 파일 내용';
    const jsonText = JSON.stringify(this.project, null, 2);
    const pretty = document.createElement('pre');
    pretty.className = 'review-json';
    pretty.textContent = jsonText.length > 80_000 ? jsonText.slice(0, 80_000) + '\n…(생략)' : jsonText;
    const copyJsonBtn = document.createElement('button');
    copyJsonBtn.className = 'btn ghost';
    copyJsonBtn.textContent = 'JSON 복사';
    copyJsonBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(jsonText);
        copyJsonBtn.textContent = '✅ 복사됨';
        setTimeout(() => (copyJsonBtn.textContent = 'JSON 복사'), 1200);
      } catch {
        copyJsonBtn.textContent = '⚠ 복사 실패';
      }
    });
    sec.append(h, copyJsonBtn, pretty);
    return sec;
  }
}

function renderVerdict(host: HTMLElement, result: VerifyResult): void {
  host.replaceChildren();
  const tag = document.createElement('span');
  tag.className = `review-verdict-tag verdict-${result.verdict}`;
  tag.textContent =
    result.verdict === 'safe' ? '✅ 안전' : result.verdict === 'unsafe' ? '⛔ 위험' : '❓ 불확실';
  const body = document.createElement('div');
  body.className = 'review-verdict-body';
  body.textContent = result.summary;
  host.append(tag, body);
  host.className = `review-ai-status verdict-${result.verdict}`;
}

let singleton: ImportReviewModal | null = null;
export function reviewAndLoad(project: Project): Promise<boolean> {
  if (!singleton) singleton = new ImportReviewModal();
  return singleton.prompt(project);
}
